import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unstable_useBlocker, useBeforeUnload } from 'react-router-dom';

import { Modal } from '../Modal';

type Props = {
  hasUnsavedChanges: boolean;
  onSave?: () => Promise<void>;
};

export const NotSavedChangesModal = ({ hasUnsavedChanges, onSave }: Props) => {
  const { t } = useTranslation('common');
  const [isSaving, setIsSaving] = useState(false);

  const { state, reset, proceed } = unstable_useBlocker(hasUnsavedChanges);

  const isBlockedByReactRouter = state === 'blocked';

  useBeforeUnload(
    useCallback(
      e => {
        if (hasUnsavedChanges && !isBlockedByReactRouter) {
          e.preventDefault();
          e.returnValue = '';
        }
      },
      [hasUnsavedChanges, isBlockedByReactRouter],
    ),
  );

  const handleSaveAndLeave = async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave();
      proceed?.();
    } catch {
      reset?.();
    } finally {
      setIsSaving(false);
    }
  };

  if (onSave) {
    return (
      <Modal
        isOpen={isBlockedByReactRouter}
        onClose={reset}
        size="lg"
        title={t('unsavedChanges.title')}
        defaultButtonText={t('cancel')}
        defaultButtonAction={reset}
        redButtonText={t('discardChanges')}
        redButtonAction={proceed}
        primaryButtonText={t('save')}
        primaryButtonAction={handleSaveAndLeave}
        primaryButtonIsLoading={isSaving}
        primaryButtonColor="green"
      >
        {t('unsavedChanges.description')}
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isBlockedByReactRouter}
      onClose={reset}
      size="lg"
      title={t('common:notSavedChanges')}
      defaultButtonText={t('common:back')}
      defaultButtonAction={reset}
      primaryButtonText={t('common:yesCancelChanges')}
      primaryButtonAction={proceed}
    >
      {t('notSavedChangesDescription')}
    </Modal>
  );
};
